import { commercialEditions, provisionalCommercialPolicy, type CommercialEditionId, type CommercialFeatureId, type CommercialLimits } from '../../config/commercialPlans';
import type { Workspace } from '../auth/types';
import type { LocalLicense, OrganizationSubscription, PlatformNotice, SaasDatabase, SaasPlan, SubscriptionStatus, SupportSession, UsageSnapshot } from './types';

export const SAAS_DATABASE_KEY = 'nexo.phase12.saas.database';
export const SAAS_CHANGE_EVENT = 'nexo:saas-changed';
export const EDITION_KEY = 'nexo.commercial.edition';
export const INSTALLATION_KEY = 'nexo.installation.id';

const nowIso = () => new Date().toISOString();
const id = (prefix: string) => `${prefix}_${crypto.randomUUID?.() ?? `${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`}`;
const addDays = (value: string | Date, days: number) => { const date = new Date(value); date.setDate(date.getDate() + days); return date.toISOString(); };
const addMonths = (value: string | Date, months: number) => { const date = new Date(value); date.setMonth(date.getMonth() + months); return date.toISOString(); };
const addYears = (value: string | Date, years: number) => { const date = new Date(value); date.setFullYear(date.getFullYear() + years); return date.toISOString(); };

const plansFromConfig = (): SaasPlan[] => Object.values(commercialEditions).map((edition) => ({
  id: edition.id,
  name: edition.name,
  shortName: edition.shortName,
  billing: edition.billing,
  priceCents: edition.priceCents,
  active: true,
  limits: { ...edition.limits },
  features: { ...edition.features },
  updatedAt: nowIso(),
}));

const emptyDatabase = (): SaasDatabase => ({ version: 1, plans: plansFromConfig(), subscriptions: [], licenses: [], notices: [], supportSessions: [] });

const readDatabase = (): SaasDatabase => {
  const raw = localStorage.getItem(SAAS_DATABASE_KEY);
  if (!raw) return emptyDatabase();
  try {
    const parsed = JSON.parse(raw) as SaasDatabase;
    if (parsed.version !== 1) return emptyDatabase();
    const configured = plansFromConfig();
    for (const plan of configured) if (!parsed.plans.some((item) => item.id === plan.id)) parsed.plans.push(plan);
    return parsed;
  } catch { return emptyDatabase(); }
};

const writeDatabase = (database: SaasDatabase) => {
  localStorage.setItem(SAAS_DATABASE_KEY, JSON.stringify(database));
  window.dispatchEvent(new CustomEvent(SAAS_CHANGE_EVENT));
};

export const subscribeSaas = (listener: () => void) => {
  window.addEventListener(SAAS_CHANGE_EVENT, listener);
  window.addEventListener('storage', listener);
  return () => { window.removeEventListener(SAAS_CHANGE_EVENT, listener); window.removeEventListener('storage', listener); };
};

export const getInstallationId = () => {
  let value = localStorage.getItem(INSTALLATION_KEY);
  if (!value) { value = id('install'); localStorage.setItem(INSTALLATION_KEY, value); }
  return value;
};

export const ensureOrganizationSubscription = (organizationId: string): OrganizationSubscription => {
  const database = readDatabase();
  let subscription = database.subscriptions.find((item) => item.organizationId === organizationId);
  if (!subscription) {
    const startedAt = nowIso();
    subscription = {
      id: id('sub'), organizationId, planId: 'trial', status: 'trialing', startedAt,
      trialEndsAt: addDays(startedAt, provisionalCommercialPolicy.trialDays),
      graceEndsAt: addDays(startedAt, provisionalCommercialPolicy.trialDays + provisionalCommercialPolicy.trialReadOnlyGraceDays),
      notes: 'Prueba creada automáticamente al terminar el onboarding.', updatedAt: startedAt,
    };
    database.subscriptions.push(subscription);
    writeDatabase(database);
  }
  return normalizeSubscription(subscription);
};

const normalizeSubscription = (subscription: OrganizationSubscription): OrganizationSubscription => {
  const now = Date.now();
  let next = { ...subscription };
  if (next.planId === 'trial' && !['suspended','cancelled'].includes(next.status)) {
    const trialEnd = next.trialEndsAt ? new Date(next.trialEndsAt).getTime() : 0;
    const graceEnd = next.graceEndsAt ? new Date(next.graceEndsAt).getTime() : 0;
    if (trialEnd && now <= trialEnd) next.status = 'trialing';
    else if (graceEnd && now <= graceEnd) next.status = 'grace';
    else next.status = 'past-due';
  }
  if ((next.planId === 'cloud-monthly' || next.planId === 'cloud-annual') && next.currentPeriodEnd && now > new Date(next.currentPeriodEnd).getTime() && next.status === 'active') next.status = 'past-due';
  return next;
};

export const getSaasDatabase = () => {
  const database = readDatabase();
  let changed = false;
  database.subscriptions = database.subscriptions.map((item) => {
    const normalized = normalizeSubscription(item);
    if (normalized.status !== item.status) changed = true;
    return normalized;
  });
  if (changed) writeDatabase(database);
  return database;
};

export const getSubscription = (organizationId: string) => ensureOrganizationSubscription(organizationId);
export const getPlan = (planId: CommercialEditionId) => getSaasDatabase().plans.find((item) => item.id === planId) ?? plansFromConfig().find((item) => item.id === planId)!;

export const getSubscriptionAccess = (organizationId: string) => {
  const subscription = getSubscription(organizationId);
  const plan = getPlan(subscription.planId);
  const readOnly = subscription.status === 'grace';
  const blocked = subscription.status === 'past-due' || subscription.status === 'suspended' || subscription.status === 'cancelled';
  return { subscription, plan, readOnly, blocked, localOnly: subscription.planId === 'local-perpetual' };
};

export const setActiveEdition = (organizationId: string) => {
  const subscription = getSubscription(organizationId);
  localStorage.setItem(EDITION_KEY, subscription.planId);
};

export const hasFeature = (organizationId: string, feature: CommercialFeatureId) => {
  const { plan, blocked } = getSubscriptionAccess(organizationId);
  return !blocked && Boolean(plan.features[feature]);
};

export const assertWritableSubscription = (organizationId: string) => {
  const access = getSubscriptionAccess(organizationId);
  if (access.blocked) throw new Error('La suscripción no permite registrar cambios. Elige un plan o solicita reactivación.');
  if (access.readOnly) throw new Error('La prueba terminó y está en periodo de gracia de solo lectura. Elige un plan para continuar operando.');
};

export const assertPlanLimit = (organizationId: string, limit: keyof CommercialLimits, currentCount: number, increment = 1) => {
  const { plan } = getSubscriptionAccess(organizationId);
  const maximum = plan.limits[limit];
  if (currentCount + increment > maximum) throw new Error(`El plan permite hasta ${maximum} ${limit}. Cambia de plan para continuar.`);
};

export const calculateUsage = (organizationId: string, workspace?: Workspace | null): UsageSnapshot => {
  const businesses = workspace?.businesses.filter((item) => item.organizationId === organizationId) ?? [];
  const businessIds = new Set(businesses.map((item) => item.id));
  const branches = workspace?.branches.filter((item) => businessIds.has(item.businessId)) ?? [];
  const users = new Set((workspace?.memberships ?? []).filter((item) => item.organizationId === organizationId).map((item) => item.userId)).size;
  let products = 0;
  try {
    const catalog = JSON.parse(localStorage.getItem('nexo.phase3.catalog.database') ?? '{"catalogs":{}}') as { catalogs?: Record<string,{products?:unknown[]}> };
    for (const businessId of businessIds) products += catalog.catalogs?.[businessId]?.products?.length ?? 0;
  } catch { products = 0; }
  let aiActions = 0;
  try {
    const phase8 = JSON.parse(localStorage.getItem('nexo.phase8.analytics.database') ?? '{}') as { businesses?: Record<string,{aiActions?:unknown[]}> };
    for (const businessId of businessIds) aiActions += phase8.businesses?.[businessId]?.aiActions?.length ?? 0;
  } catch { aiActions = 0; }
  const storageBytes = Object.keys(localStorage).reduce((sum, key) => sum + (localStorage.getItem(key)?.length ?? 0) * 2, 0);
  return { organizationId, businesses: businesses.length, branches: branches.length, users, products, storageMb: Math.round((storageBytes / 1024 / 1024) * 100) / 100, aiActions };
};

export const savePlan = (input: { id: CommercialEditionId; priceCents: number | null; limits: CommercialLimits; active: boolean }) => {
  const database = readDatabase();
  const plan = database.plans.find((item) => item.id === input.id);
  if (!plan) throw new Error('No se encontró el plan.');
  if (input.priceCents !== null && input.priceCents < 0) throw new Error('El precio no puede ser negativo.');
  plan.priceCents = input.priceCents;
  plan.limits = { ...input.limits };
  plan.active = input.active;
  plan.updatedAt = nowIso();
  writeDatabase(database);
  return plan;
};

export const assignPlan = (organizationId: string, planId: CommercialEditionId, notes = '') => {
  const database = readDatabase();
  const plan = database.plans.find((item) => item.id === planId);
  if (!plan || !plan.active) throw new Error('El plan no está disponible.');
  if (planId === 'local-perpetual' && !database.licenses.some((item) => item.organizationId === organizationId && item.status === 'active')) throw new Error('Primero activa una licencia local firmada para esta instalación.');
  let subscription = database.subscriptions.find((item) => item.organizationId === organizationId);
  const timestamp = nowIso();
  const periodEnd = plan.billing === 'monthly' ? addMonths(timestamp, 1) : plan.billing === 'annual' ? addYears(timestamp, 1) : undefined;
  if (!subscription) {
    subscription = { id: id('sub'), organizationId, planId, status: planId === 'local-perpetual' ? 'local-active' : planId === 'trial' ? 'trialing' : 'active', startedAt: timestamp, notes, updatedAt: timestamp };
    database.subscriptions.push(subscription);
  }
  subscription.planId = planId;
  subscription.status = planId === 'local-perpetual' ? 'local-active' : planId === 'trial' ? 'trialing' : 'active';
  subscription.currentPeriodStart = periodEnd ? timestamp : undefined;
  subscription.currentPeriodEnd = periodEnd;
  subscription.trialEndsAt = planId === 'trial' ? addDays(timestamp, provisionalCommercialPolicy.trialDays) : undefined;
  subscription.graceEndsAt = planId === 'trial' ? addDays(timestamp, provisionalCommercialPolicy.trialDays + provisionalCommercialPolicy.trialReadOnlyGraceDays) : undefined;
  subscription.suspendedAt = undefined;
  subscription.suspendedReason = undefined;
  subscription.cancelledAt = undefined;
  subscription.notes = notes;
  subscription.updatedAt = timestamp;
  writeDatabase(database);
  return subscription;
};

export const extendTrial = (organizationId: string, days: number) => {
  if (days <= 0 || days > 90) throw new Error('La extensión debe ser de 1 a 90 días.');
  const database = readDatabase();
  const subscription = database.subscriptions.find((item) => item.organizationId === organizationId) ?? ensureOrganizationSubscription(organizationId);
  const base = subscription.trialEndsAt && new Date(subscription.trialEndsAt).getTime() > Date.now() ? subscription.trialEndsAt : nowIso();
  subscription.planId = 'trial'; subscription.status = 'trialing'; subscription.trialEndsAt = addDays(base, days); subscription.graceEndsAt = addDays(subscription.trialEndsAt, provisionalCommercialPolicy.trialReadOnlyGraceDays); subscription.updatedAt = nowIso();
  writeDatabase(database); return subscription;
};

export const suspendOrganization = (organizationId: string, reason: string) => {
  if (!reason.trim()) throw new Error('Escribe el motivo de suspensión.');
  const database = readDatabase(); const subscription = database.subscriptions.find((item) => item.organizationId === organizationId) ?? ensureOrganizationSubscription(organizationId);
  subscription.status = 'suspended'; subscription.suspendedAt = nowIso(); subscription.suspendedReason = reason.trim(); subscription.updatedAt = nowIso(); writeDatabase(database); return subscription;
};
export const reactivateOrganization = (organizationId: string) => {
  const database = readDatabase(); const subscription = database.subscriptions.find((item) => item.organizationId === organizationId) ?? ensureOrganizationSubscription(organizationId);
  subscription.status = subscription.planId === 'local-perpetual' ? 'local-active' : subscription.planId === 'trial' ? 'trialing' : 'active'; subscription.suspendedAt = undefined; subscription.suspendedReason = undefined; subscription.updatedAt = nowIso(); writeDatabase(database); return subscription;
};

export const saveNotice = (input: Omit<PlatformNotice,'id'|'createdAt'> & { id?: string }) => {
  const database = readDatabase();
  if (!input.title.trim() || !input.message.trim()) throw new Error('El aviso necesita título y mensaje.');
  if (input.id) database.notices = database.notices.map((item) => item.id === input.id ? { ...item, ...input } : item);
  else database.notices.unshift({ ...input, id: id('notice'), createdAt: nowIso() });
  writeDatabase(database); return database.notices[0];
};

export const startSupportSession = (organizationId: string, requestedBy: string, reason: string): SupportSession => {
  if (!reason.trim()) throw new Error('Escribe el motivo del acceso de soporte.');
  const database = readDatabase();
  const session: SupportSession = { id: id('support'), organizationId, requestedBy, reason: reason.trim(), status: 'active', startedAt: nowIso() };
  database.supportSessions.push(session); writeDatabase(database); return session;
};
export const closeSupportSession = (sessionId: string) => { const database = readDatabase(); const session = database.supportSessions.find((item) => item.id === sessionId); if (!session) throw new Error('No se encontró la sesión.'); session.status = 'closed'; session.endedAt = nowIso(); writeDatabase(database); return session; };

const bytesToBase64 = (bytes: Uint8Array) => btoa(String.fromCharCode(...bytes));
const base64ToBytes = (value: string) => Uint8Array.from(atob(value), (character) => character.charCodeAt(0));
const DEV_PUBLIC_JWK: JsonWebKey = { kty: 'EC', crv: 'P-256', x: 'R2Idyt1A9jZrD39PaJi5QfhEWEyzjoitslD1gKCi7tw', y: 'kchyBPJeeIPUUNgOB-pzqnz-BbRHRSIrDNpa2XDr86Q', ext: true, key_ops: ['verify'] };
const DEV_PRIVATE_JWK: JsonWebKey = { kty: 'EC', crv: 'P-256', x: 'R2Idyt1A9jZrD39PaJi5QfhEWEyzjoitslD1gKCi7tw', y: 'kchyBPJeeIPUUNgOB-pzqnz-BbRHRSIrDNpa2XDr86Q', d: '-T_xtSNv6P-XIgYkPtjKzqXvgMh1Fqqb-8QUDoqFd8s', ext: true, key_ops: ['sign'] };

export const issueDevelopmentLocalLicense = async (organizationId: string): Promise<LocalLicense> => {
  const database = readDatabase(); const issuedAt = nowIso(); const installationId = getInstallationId();
  const payload = JSON.stringify({ organizationId, installationId, edition: 'local-perpetual', issuedAt, maxBusinesses: 1, maxBranches: 1 });
  const key = await crypto.subtle.importKey('jwk', DEV_PRIVATE_JWK, { name: 'ECDSA', namedCurve: 'P-256' }, false, ['sign']);
  const signature = bytesToBase64(new Uint8Array(await crypto.subtle.sign({ name: 'ECDSA', hash: 'SHA-256' }, key, new TextEncoder().encode(payload))));
  const license: LocalLicense = { id: id('license'), organizationId, installationId, edition: 'local-perpetual', issuedAt, status: 'issued', signature, payload };
  database.licenses.push(license); writeDatabase(database); return license;
};

export const verifyAndActivateLocalLicense = async (licenseText: string): Promise<LocalLicense> => {
  const input = JSON.parse(licenseText) as LocalLicense;
  if (!input.payload || !input.signature) throw new Error('El archivo de licencia está incompleto.');
  const payload = JSON.parse(input.payload) as { organizationId: string; installationId: string; edition: string };
  if (payload.edition !== 'local-perpetual') throw new Error('La licencia no corresponde a NEXO Local permanente.');
  if (payload.installationId !== getInstallationId()) throw new Error('La licencia fue emitida para otra instalación.');
  const key = await crypto.subtle.importKey('jwk', DEV_PUBLIC_JWK, { name: 'ECDSA', namedCurve: 'P-256' }, false, ['verify']);
  const valid = await crypto.subtle.verify({ name: 'ECDSA', hash: 'SHA-256' }, key, base64ToBytes(input.signature), new TextEncoder().encode(input.payload));
  if (!valid) throw new Error('La firma de la licencia no es válida.');
  const database = readDatabase();
  const license = database.licenses.find((item) => item.id === input.id) ?? { ...input };
  license.status = 'active'; license.activatedAt = nowIso();
  database.licenses = database.licenses.filter((item) => item.id !== license.id); database.licenses.push(license);
  let subscription = database.subscriptions.find((item) => item.organizationId === payload.organizationId);
  if (!subscription) { subscription = { id: id('sub'), organizationId: payload.organizationId, planId: 'local-perpetual', status: 'local-active', startedAt: nowIso(), notes: '', updatedAt: nowIso() }; database.subscriptions.push(subscription); }
  subscription.planId = 'local-perpetual'; subscription.status = 'local-active'; subscription.localLicenseId = license.id; subscription.updatedAt = nowIso();
  writeDatabase(database); localStorage.setItem(EDITION_KEY, 'local-perpetual'); return license;
};

export const downloadLicenseFile = (license: LocalLicense) => {
  const blob = new Blob([JSON.stringify(license, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob); const anchor = document.createElement('a'); anchor.href = url; anchor.download = `nexo-license-${license.organizationId}.json`; anchor.click(); URL.revokeObjectURL(url);
};

export const statusLabel: Record<SubscriptionStatus,string> = { trialing: 'Prueba activa', active: 'Activa', grace: 'Solo lectura', 'past-due': 'Vencida', suspended: 'Suspendida', cancelled: 'Cancelada', 'local-active': 'Permanente local' };

export type PlatformOrganizationSummary = { id: string; name: string; ownerEmail: string; businesses: number; branches: number; users: number };
export const getPlatformOrganizations = (): PlatformOrganizationSummary[] => {
  try {
    const auth = JSON.parse(localStorage.getItem('nexo.phase2.local.database') ?? '{}') as {
      users?: { id: string; email: string }[];
      workspaces?: Record<string, Workspace>;
    };
    const result = new Map<string, PlatformOrganizationSummary>();
    for (const [userId, workspace] of Object.entries(auth.workspaces ?? {})) {
      for (const organization of workspace.organizations ?? []) {
        if (result.has(organization.id)) continue;
        const businesses = workspace.businesses.filter((item) => item.organizationId === organization.id);
        const ids = new Set(businesses.map((item) => item.id));
        result.set(organization.id, {
          id: organization.id,
          name: organization.name,
          ownerEmail: auth.users?.find((item) => item.id === organization.ownerUserId)?.email ?? auth.users?.find((item) => item.id === userId)?.email ?? 'Sin correo',
          businesses: businesses.length,
          branches: workspace.branches.filter((item) => ids.has(item.businessId)).length,
          users: new Set(workspace.memberships.filter((item) => item.organizationId === organization.id).map((item) => item.userId)).size,
        });
      }
    }
    return [...result.values()];
  } catch { return []; }
};

export const organizationIdForBusiness = (businessId: string) => {
  try {
    const auth = JSON.parse(localStorage.getItem('nexo.phase2.local.database') ?? '{}') as { workspaces?: Record<string,Workspace> };
    for (const workspace of Object.values(auth.workspaces ?? {})) {
      const business = workspace.businesses.find((item) => item.id === businessId);
      if (business) return business.organizationId;
    }
  } catch { return ''; }
  return '';
};
export const assertWritableForBusiness = (businessId: string) => {
  const organizationId = organizationIdForBusiness(businessId);
  if (organizationId) assertWritableSubscription(organizationId);
};
export const hasBusinessFeature = (businessId: string, feature: CommercialFeatureId) => {
  const organizationId = organizationIdForBusiness(businessId);
  return organizationId ? hasFeature(organizationId, feature) : true;
};
